import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import {
  getQmRecordRequest,
  listAttachmentsRequest,
  listQmReferenceClosureCodesRequest,
  listQmReferenceLocationsRequest,
  listQmReferenceReasonsRequest,
  QmAttachment,
  QmCrudStatus,
  QmRecord
} from '../api/qmCrud';
import { captureError } from '../common/sentry';
import { getQmTypeLabel } from '../common/qmTypes';
import QmDetailDialog, { OptionItem, QmDetailDialogModel } from '../pages/qm-coordinator/QmDetailDialog';
import { useAlert } from '../hooks';

type QmDetailsDialogContextValue = {
  openQmDetails: (qmId: number) => Promise<void>;
};

const STATUS_OPTIONS: QmCrudStatus[] = ['CREATED', 'PENDING', 'CLOSED'];

const sectionCardSx = {
  p: 1.5,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.paper'
} as const;

const QmDetailsDialogContext = createContext<QmDetailsDialogContextValue | null>(null);

function formatStatusLabel(status: QmCrudStatus | string | null | undefined): string {
  if (!status) return 'N/A';
  if (status === 'CREATED') return 'Open';
  if (status === 'PENDING') return 'Pending';
  if (status === 'CLOSED') return 'Closed';
  return status;
}

export function QmDetailsDialogProvider({ children }: { children: ReactNode }) {
  const alert = useAlert();

  const [selectedQm, setSelectedQm] = useState<QmRecord | null>(null);
  const [attachments, setAttachments] = useState<QmAttachment[]>([]);
  const [reasonOptions, setReasonOptions] = useState<OptionItem[]>([]);
  const [editReasonOptions, setEditReasonOptions] = useState<OptionItem[]>([]);
  const [closureOptions, setClosureOptions] = useState<OptionItem[]>([]);
  const [locationOptions, setLocationOptions] = useState<OptionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => {
    setIsOpen(false);
    setSelectedQm(null);
    setAttachments([]);
  }, []);

  const getLocationLabel = useCallback(
    (itemLocation: string | null | undefined) => {
      const value = (itemLocation || '').trim();
      if (!value) return '-';

      const normalized = value.toLowerCase();
      const matched = locationOptions.find(
        (location) =>
          location.code.trim().toLowerCase() === normalized || location.label.trim().toLowerCase() === normalized
      );

      return matched?.label || value;
    },
    [locationOptions]
  );

  const openQmDetails = useCallback(
    async (qmId: number) => {
      if (!qmId) return;
      setIsOpen(true);
      setIsLoading(true);

      try {
        const [qm, attachmentResults, inboundReasons, siteInboundReasons, closureResults, locationResults] =
          await Promise.all([
            getQmRecordRequest({ qmId }),
            listAttachmentsRequest(qmId),
            listQmReferenceReasonsRequest('INBOUND'),
            listQmReferenceReasonsRequest('SITE_INBOUND'),
            listQmReferenceClosureCodesRequest(),
            listQmReferenceLocationsRequest()
          ]);

        if (!qm) {
          throw new Error(`QM record ${qmId} was not found`);
        }

        const mergedReasons = [...inboundReasons, ...siteInboundReasons];
        const uniqueReasonOptions = Array.from(
          new Map(mergedReasons.map((item) => [item.code, { code: item.code, label: item.label }])).values()
        );
        const scopedReasons = qm.type === 'SITE_INBOUND' ? siteInboundReasons : inboundReasons;

        setSelectedQm(qm);
        setAttachments(attachmentResults || []);
        setReasonOptions(uniqueReasonOptions);
        setEditReasonOptions(scopedReasons.map((item) => ({ code: item.code, label: item.label })));
        setClosureOptions((closureResults || []).map((item) => ({ code: item.code, label: item.label })));
        setLocationOptions((locationResults || []).map((item) => ({ code: item.code, label: item.label })));
      } catch (error) {
        captureError(error, 'qm-details-dialog-openQmDetails');
        alert('Unable to load QM details', 'error');
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    },
    [alert]
  );

  const openInQmCases = useCallback(() => {
    if (!selectedQm) return;
    const qmNumber = String(selectedQm.qmNumber || selectedQm.qmId).trim();
    setIsOpen(false);
    window.history.pushState(null, '', `/qm-cases?qm=${encodeURIComponent(qmNumber)}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [selectedQm]);

  const model: QmDetailDialogModel = useMemo(
    () => ({
      selectedQm,
      isDetailsLoading: isLoading,
      canEdit: false,
      canUploadAttachments: false,
      canReprintLabels: false,
      showFilteredOutNotice: false,
      sectionCardSx,
      attachments,
      isAttachmentsLoading: isLoading,
      isUploadingAttachments: false,
      onUploadAttachments: () => undefined,
      onSelectPreview: () => undefined,
      actionComment: '',
      isAddingActionComment: false,
      onActionCommentChange: () => undefined,
      addActionComment: () => undefined,
      onReprintLabels: () => undefined,
      isReprintingLabels: false,
      editStatus: selectedQm?.status || 'CREATED',
      onEditStatusChange: () => undefined,
      editReasonCode: selectedQm?.reasonCode || '',
      onEditReasonCodeChange: () => undefined,
      editSupplierName: selectedQm?.supplierName || '',
      onEditSupplierNameChange: () => undefined,
      editContactName: selectedQm?.contactName || '',
      onEditContactNameChange: () => undefined,
      editItemLocation: selectedQm?.itemLocation || '',
      onEditItemLocationChange: () => undefined,
      editOtherInformation: selectedQm?.otherInformation || '',
      onEditOtherInformationChange: () => undefined,
      editDescription: selectedQm?.description || '',
      onEditDescriptionChange: () => undefined,
      editClosureCode: selectedQm?.closureCode || '',
      onEditClosureCodeChange: () => undefined,
      editClosureNotes: selectedQm?.closureNotes || '',
      onEditClosureNotesChange: () => undefined,
      saveQmStatusEdits: async () => false,
      saveQmDetailEdits: async () => false,
      closureOptions,
      reasonOptions,
      editReasonOptions,
      locationOptions,
      getLocationLabel,
      getQmTypeLabel,
      statusOptions: STATUS_OPTIONS,
      formatStatusLabel,
      onOpenInQmCases: selectedQm ? openInQmCases : undefined
    }),
    [
      attachments,
      closureOptions,
      editReasonOptions,
      getLocationLabel,
      isLoading,
      locationOptions,
      openInQmCases,
      reasonOptions,
      selectedQm
    ]
  );

  return (
    <QmDetailsDialogContext.Provider value={{ openQmDetails }}>
      {children}
      {isOpen ? <QmDetailDialog open={isOpen} onClose={close} model={model} /> : null}
    </QmDetailsDialogContext.Provider>
  );
}

export function useQmDetailsDialog() {
  const context = useContext(QmDetailsDialogContext);
  if (!context) {
    throw new Error('useQmDetailsDialog must be used within QmDetailsDialogProvider');
  }
  return context;
}
